import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";

/**
 * Lightweight fullscreen image lightbox. Fixes the long-standing bug where
 * clicking a listing photo on Android opened `window.open(src)` as a bare
 * resource tab and the browser showed a black / blank screen. Rendering the
 * image inside the SPA keeps it inside our proxied /api/files URL and works
 * reliably on all mobile browsers.
 */
export default function FullscreenImage({ images = [], startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(Math.max(0, Math.min(startIndex, images.length - 1)));
  const [err, setErr] = useState(false);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
      else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [images.length, onClose]);

  useEffect(() => { setErr(false); }, [idx]);

  if (!images.length) return null;
  const src = images[idx];
  const multi = images.length > 1;

  return (
    <div data-testid="fullscreen-image" className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="flex items-center justify-between px-4 md:px-8 py-3 text-white">
        <div className="text-xs opacity-80">{multi ? `${idx + 1} / ${images.length}` : ""}</div>
        <div className="flex items-center gap-2">
          <a data-testid="fullscreen-open-tab" href={src} target="_blank" rel="noreferrer"
             className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 flex items-center gap-1 text-xs">
            <Download className="h-4 w-4" /> Open
          </a>
          <button data-testid="fullscreen-close" onClick={onClose} aria-label="Close"
            className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="flex-1 relative flex items-center justify-center select-none">
        {err ? (
          <div className="text-white/80 text-center px-6">
            <div>Could not load this image.</div>
            <a href={src} target="_blank" rel="noreferrer" className="mt-3 inline-block px-4 py-2 rounded-full bg-white/10">Open in new tab</a>
          </div>
        ) : (
          <img key={src} src={src} alt="" onError={() => setErr(true)}
               className="max-h-[85vh] max-w-[95vw] object-contain" />
        )}
        {multi && (
          <>
            <button data-testid="fullscreen-prev"
              onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button data-testid="fullscreen-next"
              onClick={() => setIdx((i) => (i + 1) % images.length)}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>
      {multi && (
        <div className="pb-4 flex justify-center gap-1.5">
          {images.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2 bg-white/40"}`} />
          ))}
        </div>
      )}
    </div>
  );
}
